/**
 * StatusDropdown component for changing essay status.
 * 
 * Features:
 * - Shows current status as a badge
 * - Dropdown shows only valid transitions from current state
 * - Confirmation modal before status change
 * - Optional change summary input
 * - Loading state during API call
 * 
 * Only visible to users with 'author' role.
 * 
 * Called by:
 * - EssayDetailPage.tsx for status management
 */
import { useState } from 'react';
import { Dropdown, Button, Modal, Input, message, Space } from 'antd';
import { DownOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import type { MenuProps } from 'antd';
import type { EssayStatus, StatusAction, User } from '@compendium/shared';
import { STATUS_LABELS, STATUS_ACTION_LABELS } from '@compendium/shared';
import { changeEssayStatus } from '../services/api';
import StatusBadge from './StatusBadge';

// =============================================================================
// State Machine Configuration
// =============================================================================

/**
 * Valid transitions from each status.
 * Must match backend VALID_TRANSITIONS in workflow.ts.
 */
const VALID_TRANSITIONS: Record<EssayStatus, StatusAction[]> = {
  draft: ['submitForReview', 'publish'],
  in_review: ['approve', 'requestRevision'],
  revision: ['resubmit'],
  approved: ['publish', 'requestRevision'],
  published: ['unpublish', 'archive'],
  archived: ['unarchive'],
};

/**
 * Map actions to their resulting status.
 */
const TRANSITION_RESULTS: Record<StatusAction, EssayStatus> = {
  submitForReview: 'in_review',
  approve: 'approved',
  requestRevision: 'revision',
  resubmit: 'in_review',
  publish: 'published',
  unpublish: 'draft',
  archive: 'archived',
  unarchive: 'draft',
};

/**
 * Icons for different action types.
 */
const ACTION_ICONS: Partial<Record<StatusAction, string>> = {
  submitForReview: '📤',
  approve: '✅',
  requestRevision: '📝',
  resubmit: '🔄',
  publish: '🚀',
  unpublish: '📥',
  archive: '📦',
  unarchive: '📂',
};

// =============================================================================
// Component Props
// =============================================================================

interface StatusDropdownProps {
  /** Current essay status */
  status: EssayStatus;
  /** Essay slug for API call */
  slug: string;
  /** Current user (to check role) */
  user: User;
  /** Callback when status changes successfully */
  onStatusChange?: (newStatus: EssayStatus) => void;
  /** Whether the dropdown is disabled */
  disabled?: boolean;
}

// =============================================================================
// Component
// =============================================================================

/**
 * StatusDropdown component.
 * Allows author to change essay status through valid transitions.
 */
function StatusDropdown({
  status,
  slug,
  user,
  onStatusChange,
  disabled = false,
}: StatusDropdownProps) {
  // State for confirmation modal
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedAction, setSelectedAction] = useState<StatusAction | null>(null);
  const [changeSummary, setChangeSummary] = useState('');
  const [loading, setLoading] = useState(false);

  // Only author can change status
  const isAuthor = user.role === 'author';

  // Get valid actions for current status
  const validActions = VALID_TRANSITIONS[status] ?? [];

  /**
   * Handle menu item click - show confirmation modal.
   */
  const handleMenuClick: MenuProps['onClick'] = ({ key }) => {
    const action = key as StatusAction;
    setSelectedAction(action);
    setChangeSummary('');
    setModalVisible(true);
  };

  /**
   * Handle confirmation - make API call to change status.
   */
  const handleConfirm = async () => {
    if (!selectedAction) return;

    setLoading(true);
    try {
      const result = await changeEssayStatus(slug, selectedAction, changeSummary || undefined);
      
      if (result) {
        const newStatus = result.essay.status;
        message.success(`Status changed to ${STATUS_LABELS[newStatus]}`);
        onStatusChange?.(newStatus);
      }
      
      setModalVisible(false);
    } catch (error) {
      console.error('Failed to change status:', error);
      message.error(error instanceof Error ? error.message : 'Failed to change status');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle modal cancel.
   */
  const handleCancel = () => {
    setModalVisible(false);
    setSelectedAction(null);
    setChangeSummary('');
  };

  // Build menu items from valid actions
  const menuItems: MenuProps['items'] = validActions.map((action) => ({
    key: action,
    label: (
      <Space>
        <span>{ACTION_ICONS[action]}</span>
        <span>{STATUS_ACTION_LABELS[action]}</span>
        <span style={{ color: '#888', fontSize: '12px' }}>
          → {STATUS_LABELS[TRANSITION_RESULTS[action]]}
        </span>
      </Space>
    ),
  }));

  // If not author or no valid actions, just show the badge
  if (!isAuthor || validActions.length === 0) {
    return <StatusBadge status={status} />;
  }

  // Get the resulting status for the selected action (for modal)
  const resultingStatus = selectedAction ? TRANSITION_RESULTS[selectedAction] : null;

  return (
    <>
      {/* Dropdown trigger */}
      <Dropdown
        menu={{ items: menuItems, onClick: handleMenuClick }}
        trigger={['click']}
        disabled={disabled || loading}
      >
        <Button
          size="small"
          style={{
            padding: '0 8px',
            height: 'auto',
            border: 'none',
            background: 'transparent',
          }}
        >
          <Space size={4}>
            <StatusBadge status={status} />
            <DownOutlined style={{ fontSize: '10px', color: '#888' }} />
          </Space>
        </Button>
      </Dropdown>

      {/* Confirmation modal */}
      <Modal
        title={
          <Space>
            <ExclamationCircleOutlined style={{ color: '#faad14' }} />
            <span>Confirm Status Change</span>
          </Space>
        }
        open={modalVisible}
        onOk={handleConfirm}
        onCancel={handleCancel}
        confirmLoading={loading}
        okText="Confirm"
        cancelText="Cancel"
      >
        {selectedAction && resultingStatus && (
          <div style={{ marginBottom: '16px' }}>
            <p>
              Are you sure you want to{' '}
              <strong>{STATUS_ACTION_LABELS[selectedAction].toLowerCase()}</strong> this essay?
            </p>
            <p>
              Status will change from{' '}
              <StatusBadge status={status} size="small" /> to{' '}
              <StatusBadge status={resultingStatus} size="small" />
            </p>
          </div>
        )}

        <div>
          <label style={{ display: 'block', marginBottom: '8px', color: '#888' }}>
            Change Summary (optional):
          </label>
          <Input.TextArea
            value={changeSummary}
            onChange={(e) => setChangeSummary(e.target.value)}
            placeholder="Describe what changed or why..."
            rows={3}
            maxLength={500}
          />
        </div>
      </Modal>
    </>
  );
}

export default StatusDropdown;
